/* eslint-disable react/prop-types */
import { Link } from "react-router-dom";

const UPDATED = "16 September 2026";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-8 space-y-3 border-t border-black/10 pt-7">
      <h2 className="font-kalice text-2xl font-medium sm:text-3xl">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-black/70 sm:text-base">
        {children}
      </div>
    </section>
  );
}

export default function PrivacyStatement() {
  return (
    <main className="mx-auto w-full max-w-3xl px-5 py-16 font-sans sm:py-24">
      <header className="mb-12 space-y-5">
        <p className="text-xs uppercase tracking-[0.18em] text-black/45">
          Legal
        </p>
        <h1 className="font-kalice text-5xl font-medium leading-none sm:text-6xl">
          Privacy Notice
        </h1>
        <p className="text-sm text-black/50">Last updated {UPDATED}</p>
        <p className="max-w-2xl text-base leading-relaxed text-black/70 sm:text-lg">
          This notice explains how Cypherloom B.V., trading as Antoia
          (collectively “Antoia”, “we”, or “us”), uses personal information
          when you visit an Antoia website, claim an experience such as a
          Trionfi card, or use your member account.
        </p>
      </header>

      <div className="space-y-10">
        <Section title="Who is responsible">
          <p>
            The controller is Cypherloom B.V., trading as Antoia,
            Sint-Agathastraat 54, unit 0.16, 3037 SH Rotterdam, the
            Netherlands. Privacy questions can be sent to{" "}
            <a className="underline underline-offset-4" href="mailto:hello@antoia.com">
              hello@antoia.com
            </a>
            .
          </p>
        </Section>

        <Section title="Information we use">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              Account information, such as your name, email address,
              language, contact details, and membership status.
            </li>
            <li>
              Information used to create an experience or piece, including
              answers and words you choose, the resulting design or reading,
              and its creation history.
            </li>
            <li>
              Communications and preferences, including whether you asked to
              receive Antoia invitations and updates.
            </li>
            <li>
              Limited technical information needed to keep the website secure
              and functioning, such as authentication, device, and log data.
            </li>
          </ul>
        </Section>

        <Section title="Why we use it">
          <p>We use this information to:</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>create, save, and show your personal experiences and pieces;</li>
            <li>provide and secure your account and membership;</li>
            <li>send requested sign-in, security, support, and service messages;</li>
            <li>operate, maintain, and improve Antoia’s services; and</li>
            <li>
              send invitations and promotional updates only when you have
              chosen to receive them.
            </li>
          </ul>
          <p>
            Depending on the purpose, our legal basis is performance of our
            agreement with you, compliance with legal obligations, our
            legitimate interests in operating and securing the service, or
            your consent. You may withdraw marketing consent at any time
            without affecting your membership.
          </p>
        </Section>

        <Section title="Trionfi and public cards">
          <p>
            A claimed Trionfi card is connected to your private member
            collection. Its share page can also be viewed by anyone who has
            its public link. The public page may show the card, accession
            number, archetype, selected word labels, reading, and creation
            date. Your claim email address and account identity are not shown
            on that page or in the public Trionfi overview.
          </p>
          <p>
            Treat a share link as public. Contact us if you want us to review
            or remove public access to a card.
          </p>
        </Section>

        <Section title="Services we rely on">
          <p>
            We use specialist providers to run Antoia, including Supabase for
            authentication and data services, Vercel for website hosting,
            SendGrid for service emails, Klaviyo for optional marketing
            messages, and Sanity for managed website content where applicable.
            They process information under their own security and contractual
            safeguards. Where information is transferred outside the European
            Economic Area, we use an applicable transfer mechanism and
            safeguards where required.
          </p>
        </Section>

        <Section id="cookies" title="Cookies and local storage">
          <p>
            Antoia uses storage that is necessary for secure sign-in, language
            and interface preferences, and the reliable operation of an
            experience. We do not use optional advertising cookies unless a
            separate cookie choice is presented where required. You can clear
            website data through your browser, although doing so may sign you
            out or reset preferences.
          </p>
        </Section>

        <Section title="Retention and security">
          <p>
            We keep account and creation information while your membership or
            collection remains active and only as long as needed for the
            purposes described above. Some records may be retained longer to
            meet legal obligations, resolve disputes, protect the service, or
            demonstrate your communication choices. We use technical and
            organisational measures intended to protect this information.
          </p>
        </Section>

        <Section title="Your choices and rights">
          <p>
            Depending on the circumstances, you may ask to access, correct,
            delete, restrict, or receive a copy of your information, or object
            to certain uses. You can withdraw optional email consent through
            the unsubscribe link or your account settings. Essential account
            and security messages are not promotional emails.
          </p>
          <p>
            Contact us to exercise a right. You may also complain to the{" "}
            <a
              className="underline underline-offset-4"
              href="https://www.autoriteitpersoonsgegevens.nl/en"
              target="_blank"
              rel="noreferrer"
            >
              Dutch Data Protection Authority
            </a>
            .
          </p>
        </Section>

        <Section title="Changes">
          <p>
            We may update this notice as Antoia develops. The current version
            and update date will remain available here. If a change materially
            affects how we use your information, we will provide an
            appropriate notice.
          </p>
        </Section>
      </div>

      <div className="mt-14 border-t border-black/10 pt-7 text-sm">
        <Link className="underline underline-offset-4" to="/terms">
          Read the Antoia Membership Terms
        </Link>
      </div>
    </main>
  );
}